import React, { useContext, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ChartTypeRegistry,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DisplaySymbol } from '../../../utils/currency';
import { InputValue } from '../../../utils/data';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'src/apps/esg-ibm-flashsystems/locales/messages';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

interface Props {
  expectedTCOCost: any;
  expectedTCOCostIBM: any;
  pctTCO: number;
  inputValue: InputValue;
}

const TCOBarChart: React.FC<Props> = ({
  expectedTCOCost,
  expectedTCOCostIBM,
  pctTCO,
  inputValue,
}: Props) => {
  const [legendPosition, setLegendPosition] = React.useState<'center' | 'end'>(
    'center',
  );
  ChartJS.defaults.borderColor = '#666666';
  ChartJS.defaults.color = '#000000';
  ChartJS.defaults.font.size = 14;
  const accessibilityPlugin = {
    id: 'accessibilityPlugin',
    beforeInit: function (chart: any, options: any) {
      // Set aria-label and role attributes
      const canvas = chart.canvas;
      canvas.setAttribute('aria-label', options.ariaLabel || 'Bar Chart');
      canvas.setAttribute('role', options.role || 'img');
    },
  };

  ChartJS.register(accessibilityPlugin);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768,
  );

  const intl = useIntl();
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200) {
        setLegendPosition('center');
      } else {
        setLegendPosition('end');
      }
    };

    window.addEventListener('resize', handleResize);

    // Call the function to set the initial state
    handleResize();

    // Cleanup function for the effect
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1024);
    }

    if (typeof window !== 'undefined') {
      handleResize();
    }

    window.addEventListener('resize', handleResize);
    return () => {
      // remove event listener when the component is unmounted to not cause any memory leaks
      // otherwise the event listener will continue to be active
      window.removeEventListener('resize', handleResize);
    };
    // add `isMobile` state variable as a dependency so that
    // it is called every time the window is resized
  }, [isMobile]);

  const { currency } = inputValue;
  const options = {
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        color: '#40BFFF',
        display: true,
        formatter: function (value: string, ctx: any) {
          if (ctx.dataset.data[ctx.dataIndex] > 150000) {
            return ctx.chart.data.datasets[ctx.datasetIndex].label;
          } else {
            return '';
          }
        },
        font: {
          size: isMobile ? 8 : 10,
          style: 'normal' as const,
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        textAlign: 'center' as const,
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<keyof ChartTypeRegistry>) {
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label +=
                DisplaySymbol(currency) + context.parsed.y.toLocaleString();
            }
            return label;
          },
        },
        TitleFont: {
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        bodyFont: {
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
      },
    },
    responsive: true,
    options: {
      plugins: {
        datalabels: {
          color: 'white',
          font: {
            size: 40,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
          anchor: 'end',
          align: 'end',
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        font: {
          color: 'black',
          family: "'IBM Plex Sans Arabic', sans-serif",
        },
        grid: {
          display: false,
          drawTicks: false,
          drawBorder: false,
        },
        label: {
          display: true,
          font: {
            size: 14,
          },
        },
        ticks: {
          display: true,
          font: {
            size: 14,
            style: 'normal' as const,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
        },
      },
      y: {
        stacked: true,
        border: {
          color: 'black',
        },
        grid: {
          borderColor: '#666666',
          drawTicks: false,
          display: false,
        },
        title: {
          display: true,
          text: intl.formatMessage(messages.annual_cost_tco),
          font: {
            size: 14,
            family: "'IBM Plex Sans Arabic', sans-serif",
          },
        },
        label: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
    },
  };

  const labels = [
    intl.formatMessage(messages.comparison),
    intl.formatMessage(messages.with_ibm),
  ];

  const annualDataCostValues = [
    expectedTCOCost.annualDataCost,
    expectedTCOCostIBM.annualDataCostIBM,
  ];
  const annualSupportMaintValues = [
    expectedTCOCost.annualSupportMaint,
    expectedTCOCostIBM.annualSupportMaintIBM,
  ];
  const powerCoolingValues = [
    expectedTCOCost.powerCooling,
    expectedTCOCostIBM.powerCoolingIBM,
  ];
  const totalAdminCostsValues = [
    expectedTCOCost.totalAdminCosts,
    expectedTCOCostIBM.totalAdminCostsIBM,
  ];

  const data = {
    labels: labels,
    datasets: [
      {
        label: isMobile
          ? intl.formatMessage(messages.aocost)
          : intl.formatMessage(messages.administrator_operational_cost),
        data: totalAdminCostsValues.map((data) => data),
        backgroundColor: '#198038',
        datalabels: {
          color: 'white',
        },
      },

      {
        label: intl.formatMessage(messages.support_maintenance),
        data: annualSupportMaintValues.map((data) => data),
        backgroundColor: '#007D79',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.infrastructure),
        data: powerCoolingValues.map((data) => data),
        backgroundColor: '#0072C3',
        datalabels: {
          color: 'white',
        },
      },
      {
        label: intl.formatMessage(messages.storage),
        data: annualDataCostValues.map((data) => data),
        backgroundColor: '#0F62FE',
        datalabels: {
          color: 'white',
        },
      },
    ],
  };
  return (
    <div>
      <div className="standard-graph">
        <h2 className="graphText">
          <span className="num"> {`${pctTCO}%`}</span>
          <br />
          <span className="text">
            <FormattedMessage {...messages.lower_storage} />
          </span>
        </h2>
        <Bar options={options} plugins={[ChartDataLabels]} data={data} />
      </div>
      <div className="graph-labels two">
        <div className="graph-label">
          <div className="first-label"></div>
          <p>
            <FormattedMessage {...messages.storage} />
          </p>
        </div>
        <div className="graph-label">
          <div className="second-label"></div>
          <p>
            <FormattedMessage {...messages.infrastructure} />
          </p>
        </div>
        <div className="graph-label">
          <div className="third-label"></div>
          <p>
            <FormattedMessage {...messages.support_maintenance} />
          </p>
        </div>
        <div className="graph-label">
          <div className="fourth-label"></div>
          <p>
            <FormattedMessage {...messages.administrator_operational_cost_ao} />
          </p>
        </div>
      </div>
    </div>
  );
};

export default TCOBarChart;
